import Vue from 'vue';
import axios from 'axios';

const templateApi = axios.create({
  headers: {
    'Cache-Control': 'public, max-age=20000'
  }
});
/**
 * Helper object to manipulate objects
 * @type {{}}
 */
const helper = {
  /**
   * Get all option keys
   * @param options
   * @returns {*[]}
   */
  getOptionsKeys(options) {
    return [
      ...Object.keys((options.data && options.data()) || {}),
      ...Object.keys(options.props || {}),
    ];
  },
  /**
   * Add new property to props
   * @param src
   * @param dest
   * @param name
   */
  defineNewProperty(src, dest, name) {
    if (!dest.hasOwnProperty(name)) {
      const descriptor = Object.getOwnPropertyDescriptor(src, name);
      Object.defineProperty(dest, name, descriptor);
    }
  },
  /**
   * Merge together data props and methods
   * @param objs
   */
  mergeCompData(objs) {
    const res = {};
    objs.forEach((obj) => {
      if (obj) {
        Object.getOwnPropertyNames(obj).forEach((name) => {
          this.defineNewProperty(obj, res, name);
        });
      }
    });
    return res;
  },
  /**
   * Build new properties from parent props
   * @param obj
   * @param props
   */
  buildFromProps(obj, props) {
    const res = {};
    props.forEach((prop) => {
      this.defineNewProperty(obj, res, prop);
    });
    return res;
  },
  /**
   * Load template file from server
   * @param template
   * @param templateDirectoryName
   * @param callback
   */
  loadTemplate(template, templateDirectoryName, callback) {
    const templatePathParts = template.split('/');
    let rawTemplate = `${APP_SETTINGS.path.templateUrl}${templateDirectoryName}/${template}`;

    // if we don't have selected template version then we will use default one
    if (templatePathParts.length === 2 && templatePathParts[0] === '') {
      templatePathParts[0] = 'default';
      template = templatePathParts.join('/');
      rawTemplate = `${APP_SETTINGS.path.templateUrl}${templateDirectoryName}/${template}`;
    }
    // if we already have cached template then load it
    if (typeof this.loadedTemplatesCache[rawTemplate] !== 'undefined') {
      callback(this.loadedTemplatesCache[rawTemplate]);
      return;
    }
    let templateUrl = `${APP_SETTINGS.path.templateUrl}${templateDirectoryName}/${template}.vue`;
    if (typeof template === 'undefined' || template === '') {
      templateUrl = `${APP_SETTINGS.path.templateUrl}${templateDirectoryName}/default/default.vue`;
    }
    templateApi.get(templateUrl)
      .then((resp) => {
        this.loadedTemplatesCache[rawTemplate] = resp.data;
        callback(resp.data);
      })
      .catch((err) => {
        console.log('error', err); // eslint-disable-line
      });
  },
  /**
   * Compile data
   * @param $parent
   * @param templateString
   * @returns {{props: *, componentData: {props: T[], computed: *, components: *}}}
   */
  compileComponentData($parent, templateString) {
    const { $data, $props, $options, $listeners } = $parent;
    const methodKeys = Object.keys($options.methods || {});
    const allKeys = helper.getOptionsKeys($options);
    const methods = helper.buildFromProps($parent, methodKeys);
    const props = helper.mergeCompData([$data, $props]);
    const componentData = {
      template: templateString,
      props: allKeys,
      computed: $options.computed,
      methods,
      components: $options.components,
    };
    return { props, componentData, $listeners };
  },
  /**
   * set component name as directory name
   * @param widgetName
   */
  setNormalizedComponentName(widgetName) {
    return widgetName.split(/(?=[A-Z])/).join('-').toLowerCase();
  },
  /**
   * Cache templates here so we don't have to reload template multiple times
   */
  loadedTemplatesCache: {},
};

export default {
  data() {
    return {
      tString: '<div></div>',
      templateLoaded: false,
      templateDirectoryName: ''
    };
  },
  props: {
    tpl: String,
    templateString: String,
    onTemplateRendered: Function,
    onTemplateLoaded: Function,
    // if this prop is true then it will rerender template if tpl changes
    rerenderOnTplSwitch: {
      type: Boolean,
      default: false,
    },
    templateDirectory: String,
  },
  created() {
    this.templateDirectoryName = this.templateDirectory || helper.setNormalizedComponentName(this.$parent.$options.name);
    // if tpl is provided we will load the template file with ajax request
    // and if template string is provided then will use the template string
    if (typeof this.templateString !== 'undefined' && this.templateString) {
      this.tString = this.templateString;
    } else if (this.tpl || this.tpl === '') {
      this.updateTemplate();
    }
  },
  updated() {
    if (this.rerenderOnTplSwitch) {
      this.updateTemplate();
    }
  },
  methods: {
    /**
     * Update template
     */
    updateTemplate() {
      if (typeof this.templateString !== 'undefined' && this.templateString) {
        this.tString = this.templateString;
        this.templateLoaded = true;
        return;
      }
      helper.loadTemplate(this.tpl, this.templateDirectoryName, (data) => {
        this.tString = data;
        this.templateLoaded = true;
        if (typeof this.onTemplateLoaded === 'function') {
          this.onTemplateLoaded(data);
        }
      });
    }
  },
  render(h) {
    // here we will load different template
    // this ajax request will only execute on admin customizer side
    // as only there template can change
    if (typeof APP_SETTINGS.devEnvironment !== 'undefined' && APP_SETTINGS.devEnvironment === 'ADMIN') {
      helper.loadTemplate(this.tpl, this.templateDirectoryName, (data) => {
        this.tString = data;
      });
    }
    // load from string template if provided
    if (typeof this.templateString !== 'undefined' && this.templateString) {
      this.tString = this.templateString;
      this.templateLoaded = true;
    }
    // we will call onTemplateRendered method here when we are sure that template is loaded and rendered
    // load string template from ajax load
    if (typeof this.onTemplateRendered === 'function') {
      this.$nextTick(() => {
        if (this.templateLoaded) {
          this.onTemplateRendered();
        }
      });
    }
    const cpData = Vue.util.extend({}, helper.compileComponentData(this.$parent, this.tString));
    return h(cpData.componentData, {
      props: cpData.props,
      on: cpData.$listeners,
    });
  },
};
